import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { applicantsRequests } from "@/src/api/applicantsRequests";
import { studentsRequests } from "@/src/api/studentsRequests";
import { admissionDatabaseApi } from "@/src/api/axiosInstance";
import { tasksStatusConst } from "@/src/constants/tasksConsts";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { setAdmissionFormContext } from "@/src/store/slices/AdmissionOffice/admissionOfficeDocSlice";
import { IAdmissionGeneratedDocumentResponse } from "@/src/types/admissionOfficeGeneratedDocument";
import { parseGeneratedDocumentResponse } from "@/src/utils/admissionOffice/parseGeneratedDocumentResponse";
import {
  buildApproveUpdatesFromFormDiff,
  logApproveChangedFields,
} from "@/src/utils/admissionOffice/buildApproveUpdatesFromFormDiff";
import { joinBaseUrlAndPath } from "@/src/utils/joinUrl";
import type {
  AdmissionTaskPrefillFormBaseline,
  AdmissionTaskPrefillMapping,
  AdmissionTaskPrefillPayload,
} from "@/src/hooks/useAdmissionTaskPrefill";

const NEXT_STATUS = "document_check";

const buildAdmissionPdfUrl = (pdfPath: string) => joinBaseUrlAndPath(admissionDatabaseApi.defaults.baseURL ?? "", pdfPath);

const buildGeneratedPdfFileName = (fullName: string, title: string) => {
  const safeName = fullName.replace(/[<>:"/\\|?*]+/g, "").trim();
  const safeTitle = title.replace(/[<>:"/\\|?*]+/g, "").trim();
  if (!safeName && !safeTitle) return "document.pdf";
  if (!safeTitle) return `${safeName}.pdf`;
  if (!safeName) return `${safeTitle}.pdf`;
  return `${safeName}(${safeTitle}).pdf`;
};

export interface UseAdmissionGeneratedDocumentActionsOptions {
  getValues: () => object;
  /** When set, writes student history after approve (admission letter). */
  studentIdFormField?: string;
}

interface TaskPrefillSnapshot {
  payload: AdmissionTaskPrefillPayload;
  mapping: AdmissionTaskPrefillMapping[];
  formBaseline: AdmissionTaskPrefillFormBaseline;
}

export const useAdmissionGeneratedDocumentActions = ({
  getValues,
  studentIdFormField,
}: UseAdmissionGeneratedDocumentActionsOptions) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = usePushNotifications();
  const taskPrefillSnapshotRef = useRef<TaskPrefillSnapshot | null>(null);

  const [isGenerated, setIsGenerated] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState<IAdmissionGeneratedDocumentResponse | null>(null);
  const [isGeneratedPdfPreviewOpen, setIsGeneratedPdfPreviewOpen] = useState(false);

  const generatedPdfUrl = generatedDocument?.PDFpath ? buildAdmissionPdfUrl(generatedDocument.PDFpath) : null;

  const generatedPreviewTitle = useMemo(() => {
    if (!generatedDocument) return "Generated document preview";
    const fullName = generatedDocument.fullName?.trim() || "Document";
    const title = generatedDocument.title?.trim();
    return title ? `${fullName} — ${title}` : fullName;
  }, [generatedDocument]);

  const setTaskPrefillSnapshot = useCallback(
    (
      payload: AdmissionTaskPrefillPayload,
      mapping: AdmissionTaskPrefillMapping[],
      formBaseline: AdmissionTaskPrefillFormBaseline,
    ) => {
      taskPrefillSnapshotRef.current = { payload, mapping, formBaseline };

      const taskId = String(payload.task_id ?? "").trim();
      const applicantRaw = payload.applicant_id ?? payload.student_id;
      const applicantParsed = applicantRaw != null ? Number(applicantRaw) : NaN;
      // Defer so Header's pathname clear effect does not wipe this context.
      window.setTimeout(() => {
        dispatch(
          setAdmissionFormContext({
            taskId,
            applicantId: Number.isFinite(applicantParsed) && applicantParsed > 0 ? applicantParsed : null,
          }),
        );
      }, 0);
    },
    [dispatch],
  );

  /** Clears generated PDF/preview only. Keeps task prefill snapshot for Approve diff. */
  const resetGeneratedState = useCallback(() => {
    setGeneratedDocument(null);
    setIsGenerated(false);
    setIsGeneratedPdfPreviewOpen(false);
  }, []);

  const onDocumentGenerated = useCallback((rawResponse: unknown) => {
    const parsed = parseGeneratedDocumentResponse(rawResponse);
    if (!parsed) {
      setGeneratedDocument(null);
      setIsGenerated(false);
      setIsGeneratedPdfPreviewOpen(false);
      return;
    }
    setGeneratedDocument(parsed);
    setIsGenerated(true);
    setIsGeneratedPdfPreviewOpen(true);
  }, []);

  const lookupDocumentIdByPdfPath = useCallback(async (pdfPath: string): Promise<number | null> => {
    const targetPath = pdfPath.replace(/^\/+/, "");
    try {
      const listing = await admissionDatabaseRequests.getListDefault("1");
      const docs = listing?.docsArray ?? [];
      const match = docs.find((doc) => {
        if (!doc.pdf_path) return false;
        return doc.pdf_path.replace(/^\/+/, "") === targetPath;
      });
      return typeof match?.id === "number" ? match.id : null;
    } catch (error) {
      console.warn("[AdmissionOffice] Could not look up document id by pdf_path:", error);
      return null;
    }
  }, []);

  const handleDownloadGeneratedPdf = useCallback(async () => {
    if (!generatedDocument?.PDFpath || !generatedPdfUrl) return;

    dispatch(setIsPreloyder(true));
    try {
      const response = await fetch(generatedPdfUrl);
      if (!response.ok) throw new Error("Failed to download file");

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = buildGeneratedPdfFileName(generatedDocument.fullName, generatedDocument.title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("PDF download error:", error);
      notify({
        title: "Download failed",
        message: "Could not download generated PDF.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [dispatch, generatedDocument, generatedPdfUrl, notify]);

  const handleRejectGeneratedDocument = useCallback(async () => {
    if (!generatedDocument) return;
    const isConfirmed = window.confirm("Are you sure you want to delete this document?");
    if (!isConfirmed) return;

    dispatch(setIsPreloyder(true));
    try {
      let documentId: number | null =
        typeof generatedDocument.id === "number"
          ? generatedDocument.id
          : typeof generatedDocument.document_id === "number"
            ? generatedDocument.document_id
            : typeof generatedDocument.data?.id === "number"
              ? generatedDocument.data.id
              : null;

      if (documentId == null && generatedDocument.PDFpath) {
        documentId = await lookupDocumentIdByPdfPath(generatedDocument.PDFpath);
      }

      if (documentId == null) {
        notify({
          title: "Delete failed",
          message: "Could not resolve document id. Try again or remove via Admissions Office Database.",
          type: "error",
        });
        return;
      }

      const userOffice = localStorage.getItem("userID") || "without login";
      await admissionDatabaseRequests.deleteCurrentDocument(documentId, userOffice);

      notify({
        title: "Document discarded",
        message: "Generated document has been deleted. You can keep editing.",
        type: "success",
      });
      resetGeneratedState();
    } catch (error) {
      console.error("[AdmissionOffice] Failed to delete generated document:", error);
      notify({
        title: "Delete failed",
        message: "Could not delete the generated document.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [dispatch, generatedDocument, lookupDocumentIdByPdfPath, notify, resetGeneratedState]);

  const handleApproveGeneratedDocument = useCallback(async () => {
    if (!generatedPdfUrl) return;

    const formValues = getValues() as Record<string, unknown>;
    const snapshot = taskPrefillSnapshotRef.current;
    const taskIdValue = String(formValues.task_id ?? snapshot?.payload.task_id ?? "").trim();
    if (!taskIdValue) {
      notify({
        title: "Cannot approve",
        message: "Task Id is empty. Fill it in to attach the document to a task.",
        type: "error",
      });
      return;
    }

    const nextStatusLabel = tasksStatusConst.find((item) => item.id === NEXT_STATUS)?.label ?? NEXT_STATUS;

    if (!snapshot) {
      console.warn("[AdmissionOffice Approve] Task prefill snapshot missing — diff uses empty baseline.");
    }

    const { applicantPayload, taskPayload, applicantChanges, taskFormChanges } =
      buildApproveUpdatesFromFormDiff({
        formBaseline: snapshot?.formBaseline ?? {},
        formValues,
        attachedFile: generatedPdfUrl,
        nextStatus: NEXT_STATUS,
      });

    const applicantIdRaw =
      formValues.applicant_id ??
      snapshot?.payload.applicant_id ??
      formValues.student_id ??
      snapshot?.payload.student_id;
    const applicantId = applicantIdRaw != null ? Number(applicantIdRaw) : NaN;
    const hasApplicantChanges = Object.keys(applicantPayload).length > 0;

    logApproveChangedFields({
      applicantId: Number.isFinite(applicantId) ? applicantId : "?",
      taskId: taskIdValue,
      applicantPayload,
      taskPayload,
      applicantChanges,
      taskFormChanges,
      nextStatus: NEXT_STATUS,
      attachedFile: generatedPdfUrl,
    });

    dispatch(setIsPreloyder(true));
    try {
      if (hasApplicantChanges) {
        if (!Number.isFinite(applicantId)) {
          notify({
            title: "Cannot approve",
            message: "Applicant ID is missing. Cannot save applicant field changes.",
            type: "error",
          });
          return;
        }

        const applicantUpdateResult = await applicantsRequests.update(applicantId, applicantPayload);
        if (applicantUpdateResult == null) {
          notify({
            title: "Approval failed",
            message: "Could not update applicant fields.",
            type: "error",
          });
          return;
        }
      }

      await tasksPageRequests.update(taskIdValue, taskPayload);

      if (studentIdFormField) {
        const rawStudentId = formValues[studentIdFormField];
        const parsedStudentId = rawStudentId != null ? Number(rawStudentId) : NaN;
        if (Number.isFinite(parsedStudentId)) {
          await studentsRequests.createHistory({
            student_id: parsedStudentId,
            text: `Changes have been made to status updated to "${nextStatusLabel}"`,
          });
        }
      }

      notify({
        title: "Task updated",
        message: hasApplicantChanges
          ? `Task ${taskIdValue} marked as "${nextStatusLabel}", document attached, applicant updated.`
          : `Task ${taskIdValue} marked as "${nextStatusLabel}" and document attached.`,
        type: "success",
      });
      setIsGeneratedPdfPreviewOpen(false);
      taskPrefillSnapshotRef.current = null;
      navigate("/tasks");
    } catch (error) {
      console.error("[AdmissionOffice] Failed to approve generated document:", error);
      notify({
        title: "Approval failed",
        message: "Could not update the task. Please try again.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [dispatch, generatedPdfUrl, getValues, navigate, notify, studentIdFormField]);

  return {
    isGenerated,
    setIsGenerated,
    generatedDocument,
    generatedPdfUrl,
    isGeneratedPdfPreviewOpen,
    setIsGeneratedPdfPreviewOpen,
    generatedPreviewTitle,
    resetGeneratedState,
    setTaskPrefillSnapshot,
    onDocumentGenerated,
    handleDownloadGeneratedPdf,
    handleApproveGeneratedDocument,
    handleRejectGeneratedDocument,
  };
};
